/**
 * Page de consentement pour l'entrée dans le dispositif.
 * Accessible SANS connexion via : /consentement/:token
 *
 * Flux :
 *   1. Charger le formulaire depuis GET /api/entree-dispositif/formulaire/{token}
 *   2. Afficher la présentation du dispositif
 *   3. Si "J'accepte" → demander signature → POST /api/entree-dispositif/repondre
 *   4. Si "Je refuse"  → demander confirmation + motif → POST → afficher lien formulaire de sortie
 */
import React, { useState, useEffect, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ─── Utilitaires ─────────────────────────────────────────────────────────────
const api = axios.create({ baseURL: API_BASE });

// ─── Composant principal ─────────────────────────────────────────────────────
export default function ConsentementDispositif({ token }) {
  const [step, setStep]       = useState('loading'); // loading | form | sign | refuse_confirm | done_accept | done_refuse | error | already_done
  const [formData, setFormData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [motifRefus, setMotifRefus] = useState('');
  const [lienSortie, setLienSortie] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const sigRef = useRef(null);

  // ── Chargement du formulaire ──────────────────────────────────────────────
  useEffect(() => {
    if (!token) { setStep('error'); setErrorMsg('Lien invalide.'); return; }

    api.get(`/entree-dispositif/formulaire/${token}`)
      .then(({ data }) => {
        if (data.statut !== 'en_attente') {
          setStep('already_done');
          setFormData(data);
        } else {
          setFormData(data);
          setStep('form');
        }
      })
      .catch(() => {
        setStep('error');
        setErrorMsg('Ce lien est invalide ou a expiré. Contactez votre référent.');
      });
  }, [token]);

  // ── Soumission acceptation ─────────────────────────────────────────────────
  const handleAccept = async () => {
    if (sigRef.current?.isEmpty()) {
      alert('Veuillez apposer votre signature avant de valider.');
      return;
    }
    setSubmitting(true);
    try {
      // react-signature-canvas v1.1.0-alpha n'expose plus getTrimmedCanvas() :
      // on utilise toDataURL() directement sur l'instance.
      const signature = sigRef.current.toDataURL('image/png');
      if (!signature || signature.length < 100) {
        alert('La signature n\'a pas pu être enregistrée. Veuillez signer à nouveau.');
        setSubmitting(false);
        return;
      }
      await api.post('/entree-dispositif/repondre', {
        token,
        decision: 'accepte',
        signature,
      });
      setStep('done_accept');
    } catch (e) {
      console.error('[Consentement] erreur soumission :', e);
      const detail = e?.response?.data?.detail || e?.message || '';
      alert(`Une erreur est survenue lors de l'enregistrement de la signature.${detail ? '\n\nDétail : ' + detail : ''}\n\nVeuillez réessayer ou contacter votre référent.`);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Soumission refus ───────────────────────────────────────────────────────
  const handleRefuse = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/entree-dispositif/repondre', {
        token,
        decision: 'refuse',
        commentaire: motifRefus || null,
      });
      setLienSortie(data.lien_formulaire_sortie || '');
      setStep('done_refuse');
    } catch (e) {
      alert("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── UI helpers ─────────────────────────────────────────────────────────────
  const prenom = formData?.apprenant_prenom || '';
  const referent = formData?.referent_nom || 'votre référent';

  // ─────────────────────────────────────────────────────────────────────────
  if (step === 'loading') return <Screen><Spinner /></Screen>;

  if (step === 'error') return (
    <Screen>
      <Card>
        <div className="text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Lien invalide</h2>
          <p className="text-slate-600">{errorMsg}</p>
        </div>
      </Card>
    </Screen>
  );

  if (step === 'already_done') return (
    <Screen>
      <Card>
        <div className="text-center">
          <div className="text-5xl mb-4">{formData?.decision === 'accepte' ? '✅' : '❌'}</div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Formulaire déjà complété</h2>
          <p className="text-slate-600">Vous avez déjà répondu à cette invitation.</p>
        </div>
      </Card>
    </Screen>
  );

  if (step === 'done_accept') return (
    <Screen>
      <Card>
        <div className="text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-emerald-700 mb-3">Bienvenue dans le dispositif !</h2>
          <p className="text-slate-700 leading-relaxed">
            Bonjour <strong>{prenom}</strong>,<br />
            votre entrée dans le dispositif a bien été enregistrée.<br />
            {referent} a été notifié(e) et vous contactera prochainement.
          </p>
          <div className="mt-6 bg-emerald-50 rounded-xl p-4 text-emerald-700 text-sm">
            Votre consentement et signature ont été enregistrés avec succès.
          </div>
        </div>
      </Card>
    </Screen>
  );

  if (step === 'done_refuse') return (
    <Screen>
      <Card>
        <div className="text-center">
          <div className="text-5xl mb-4">🤝</div>
          <h2 className="text-xl font-bold text-slate-800 mb-3">Votre réponse a été enregistrée</h2>
          <p className="text-slate-600 leading-relaxed">
            Nous respectons votre décision. {referent} a été informé(e).
          </p>
          {lienSortie && (
            <div className="mt-6">
              <p className="text-sm text-slate-500 mb-3">
                Si vous souhaitez, vous pouvez compléter le formulaire de sortie du dispositif :
              </p>
              <a
                href={lienSortie}
                className="inline-block bg-slate-700 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-800 transition-colors"
              >
                Compléter le formulaire de sortie
              </a>
            </div>
          )}
        </div>
      </Card>
    </Screen>
  );

  if (step === 'refuse_confirm') return (
    <Screen>
      <Card>
        <button
          onClick={() => setStep('form')}
          className="text-slate-400 hover:text-slate-600 text-sm mb-4 flex items-center gap-1"
        >
          ← Retour
        </button>
        <h2 className="text-xl font-bold text-slate-800 mb-2">Confirmer votre refus</h2>
        <p className="text-slate-600 mb-4 text-sm">
          Vous pouvez indiquer un motif (facultatif) :
        </p>
        <textarea
          value={motifRefus}
          onChange={e => setMotifRefus(e.target.value)}
          placeholder="Ex : Je ne souhaite pas participer pour l'instant…"
          className="w-full border border-slate-200 rounded-xl p-3 text-sm min-h-[100px] focus:ring-2 focus:ring-blue-500 outline-none resize-none"
        />
        <div className="flex gap-3 mt-4">
          <button
            onClick={() => setStep('form')}
            className="flex-1 border border-slate-300 text-slate-700 py-3 rounded-xl font-medium hover:bg-slate-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleRefuse}
            disabled={submitting}
            className="flex-1 bg-red-500 text-white py-3 rounded-xl font-medium hover:bg-red-600 transition-colors disabled:opacity-60"
          >
            {submitting ? 'Envoi…' : 'Confirmer le refus'}
          </button>
        </div>
      </Card>
    </Screen>
  );

  if (step === 'sign') return (
    <Screen>
      <Card>
        <button
          onClick={() => setStep('form')}
          className="text-slate-400 hover:text-slate-600 text-sm mb-4 flex items-center gap-1"
        >
          ← Retour
        </button>
        <h2 className="text-xl font-bold text-slate-800 mb-1">Apposez votre signature</h2>
        <p className="text-slate-500 text-sm mb-4">
          Dessinez votre signature dans le cadre ci-dessous pour confirmer votre consentement.
        </p>

        <div className="border-2 border-blue-200 rounded-xl overflow-hidden bg-white">
          <SignatureCanvas
            ref={sigRef}
            penColor="#1e3a5f"
            canvasProps={{ className: 'w-full', style: { height: 160, width: '100%' } }}
          />
        </div>

        <button
          onClick={() => sigRef.current?.clear()}
          className="mt-2 text-xs text-slate-400 hover:text-slate-600"
        >
          Effacer et recommencer
        </button>

        <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700">
          En signant, je confirme avoir lu et compris la présentation du dispositif
          ainsi que la notice d'information sur l'utilisation de mes données,
          et je consens librement à y participer.
        </div>

        <button
          onClick={handleAccept}
          disabled={submitting}
          className="mt-4 w-full bg-blue-600 text-white py-4 rounded-xl font-semibold text-lg
                     hover:bg-blue-700 transition-colors disabled:opacity-60 shadow-lg shadow-blue-200"
        >
          {submitting ? 'Enregistrement…' : '✅ Je valide mon entrée dans le dispositif'}
        </button>
      </Card>
    </Screen>
  );

  // ── Step principal : présentation + choix ──────────────────────────────────
  return (
    <Screen>
      {/* En-tête */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4 shadow-lg shadow-blue-200">
          <span className="text-3xl">🤝</span>
        </div>
        <h1 className="text-2xl font-bold text-slate-800">Dispositif d'accompagnement</h1>
        <p className="text-slate-500 mt-1">SOF Formation — Insertion professionnelle</p>
      </div>

      <Card>
        <h2 className="text-lg font-semibold text-slate-800 mb-1">
          Bonjour {prenom} 👋
        </h2>
        <p className="text-slate-600 text-sm mb-4">
          {referent} vous invite à rejoindre le dispositif d'accompagnement.
        </p>

        {formData?.message_personnalise && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4 text-sm text-blue-800">
            <strong>Message de {referent} :</strong><br />
            {formData.message_personnalise}
          </div>
        )}

        {/* Présentation du dispositif */}
        <div className="bg-slate-50 rounded-xl p-4 mb-5">
          <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <span>📋</span> Le dispositif en bref
          </h3>
          <ul className="space-y-2">
            {[
              { icon: '👤', text: 'Un suivi individuel par un intervenant dédié' },
              { icon: '📚', text: 'Des ateliers collectifs (CV, entretien, recherche d\'emploi…)' },
              { icon: '💼', text: 'Un accès exclusif à notre Bourse Emploi partenaires' },
              { icon: '📝', text: 'Un accompagnement dans vos démarches administratives' },
              { icon: '🎯', text: 'Un bilan de compétences personnalisé' },
            ].map(({ icon, text }) => (
              <li key={text} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="flex-shrink-0">{icon}</span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-slate-500">
            Participation entièrement <strong>volontaire et gratuite</strong>.
            Vous pouvez quitter le dispositif à tout moment.
          </p>
        </div>

        {/* Notice d'information RGPD — texte réellement opposable, servi par l'API
            et versionné côté serveur (voir NOTICES_INFORMATION). */}
        {formData?.notice_information && (
          <NoticeInformation notice={formData.notice_information} />
        )}

        {/* Boutons de décision */}
        <div className="space-y-3">
          <button
            onClick={() => setStep('sign')}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-semibold text-base
                       hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200 active:scale-[.98]"
          >
            ✅ J'accepte d'entrer dans le dispositif
          </button>
          <button
            onClick={() => setStep('refuse_confirm')}
            className="w-full border border-slate-200 text-slate-600 py-3 rounded-xl font-medium text-sm
                       hover:bg-slate-50 transition-colors active:scale-[.98]"
          >
            Je ne souhaite pas participer
          </button>
        </div>
      </Card>
    </Screen>
  );
}

// ─── Notice d'information RGPD ────────────────────────────────────────────────
// Dépliable : visible et accessible sans alourdir l'écran de décision. Le texte
// vient du serveur, qui en conserve la version pour le PDF du consentement.
function NoticeInformation({ notice }) {
  const [ouverte, setOuverte] = useState(false);

  return (
    <div className="border border-slate-200 rounded-xl mb-5 overflow-hidden">
      <button
        onClick={() => setOuverte((v) => !v)}
        className="w-full flex items-center justify-between gap-2 p-4 text-left
                   hover:bg-slate-50 transition-colors"
        aria-expanded={ouverte}
      >
        <span className="flex items-center gap-2 font-semibold text-sm text-slate-700">
          <span>🔒</span> {notice.titre}
        </span>
        <span className="text-slate-400 text-xs flex-shrink-0">
          {ouverte ? 'Réduire ▲' : 'Lire ▼'}
        </span>
      </button>

      {!ouverte && (
        <p className="px-4 pb-4 text-xs text-slate-500">
          Qui utilise vos données, pour quoi, qui y a accès, combien de temps,
          et quels sont vos droits.
        </p>
      )}

      {ouverte && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          {notice.sections.map((section) => (
            <div key={section.titre}>
              <h4 className="text-xs font-semibold text-slate-700 mb-1">
                {section.titre}
              </h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                {section.contenu}
              </p>
            </div>
          ))}
          <p className="text-[11px] text-slate-400 pt-1">
            Version {notice.version} — en vigueur depuis le{' '}
            {notice.date_entree_en_vigueur}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Composants de mise en page ───────────────────────────────────────────────
function Screen({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-slate-100 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {children}
      </div>
      <p className="mt-6 text-xs text-slate-400">
        SOF Formation · Données protégées RGPD
      </p>
    </div>
  );
}

function Card({ children }) {
  return (
    <div className="bg-white rounded-2xl shadow-xl shadow-slate-200 p-6">
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex flex-col items-center gap-3 py-12">
      <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      <p className="text-slate-500 text-sm">Chargement…</p>
    </div>
  );
}
